/**
 * useSEO Hook
 */

import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import seoConfig from '../config/seoConfig';
import {
  generateCanonicalUrl,
  truncateMetaDescription,
  truncateMetaTitle,
  formatKeywords,
  generateRobotsContent,
} from '../utils/seoUtils';
import { generateHreflangTags } from '../utils/hreflangGenerator';
import { isWordPressUrl, getCanonicalForWordPressUrl } from '../utils/wordpressUrlDetector';
import languageConfig from '../config/languageConfig';

function pathnameWithoutLangPrefix(pathname) {
  const segments = String(pathname || '/').split('/').filter(Boolean);
  if (segments.length && languageConfig.getUrlCodes().includes(segments[0])) {
    const rest = segments.slice(1).join('/');
    return rest ? `/${rest}` : '/';
  }
  return pathname || '/';
}

export const useSEO = (seoData = {}) => {
  const location = useLocation();
  const routePath = pathnameWithoutLangPrefix(location.pathname);

  const {
    title,
    description,
    keywords = [],
    image,
    type = 'website',
    url,
    canonical,
    noindex = false,
    nofollow = false,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    ogUrl,
    twitterCard = 'summary_large_image',
    twitterTitle,
    twitterDescription,
    twitterImage,
    schema,
    titleTemplate = 'default',
    hreflang,
    hreflangLanguages,
    hreflangXDefault,
  } = seoData;

  const routeConfig = seoConfig.routes[routePath] || {};
  const [hasPrerenderedHead] = useState(
    () => typeof document !== 'undefined' && !!document.querySelector('meta[name="seo-prerendered"]')
  );
  const hasPageMetadata = !!(
    title ||
    description ||
    canonical ||
    url ||
    noindex ||
    Object.keys(routeConfig).length
  );

  useEffect(() => {
    if (hasPrerenderedHead && !hasPageMetadata) return;
    document
      .querySelectorAll('meta[name="seo-prerendered"], meta[name="description"], meta[name="keywords"], meta[name="robots"], meta[property^="og:"], meta[name^="twitter:"], link[rel="canonical"], link[rel="alternate"], script[data-seo-prerendered], title')
      .forEach((element) => {
        if (!element.hasAttribute('data-rh')) element.remove();
      });
  }, [hasPageMetadata, hasPrerenderedHead]);

  const finalTitle = truncateMetaTitle(title || routeConfig.title || seoConfig.defaultTitle);
  const formattedTitle =
    titleTemplate !== 'default' && title ? `${title} | ${seoConfig.siteName}` : finalTitle;

  const finalDescription = truncateMetaDescription(
    description || routeConfig.description || seoConfig.defaultDescription
  );

  const finalKeywords = formatKeywords(
    keywords.length > 0 ? keywords : routeConfig.keywords || []
  ).concat(seoConfig.defaultKeywords);

  const finalImage = image || ogImage || seoConfig.defaultImage;

  const isWordPress = isWordPressUrl(location.pathname);
  const wordPressCanonical = isWordPress
    ? getCanonicalForWordPressUrl(location.pathname, seoConfig.siteUrl)
    : null;

  const finalNoindex = noindex || isWordPress;
  const finalUrl = canonical || url || generateCanonicalUrl(location.pathname, seoConfig.siteUrl);
  const finalCanonical = canonical || wordPressCanonical || finalUrl;

  const finalOgTitle = truncateMetaTitle(ogTitle || finalTitle);
  const finalOgDescription = truncateMetaDescription(ogDescription || finalDescription);
  const finalOgImage = ogImage || finalImage;
  const finalOgType = ogType || type || routeConfig.type || 'website';
  const finalOgUrl = ogUrl || finalUrl;

  const finalTwitterTitle = truncateMetaTitle(twitterTitle || finalOgTitle);
  const finalTwitterDescription = truncateMetaDescription(twitterDescription || finalOgDescription);
  const finalTwitterImage = twitterImage || finalOgImage;

  const robotsContent = generateRobotsContent({ noindex: finalNoindex, nofollow });

  const shouldHreflang =
    hreflang !== false && !finalNoindex && !isWordPress;
  const canManageHead = !hasPrerenderedHead || hasPageMetadata;

  const hreflangPath = routePath.startsWith('/') ? routePath : `/${routePath}`;

  const hreflangTags = shouldHreflang
    ? generateHreflangTags(hreflangPath, {
        baseUrl: seoConfig.siteUrl,
        languages: hreflangLanguages,
        xDefault: hreflangXDefault,
      })
    : [];

  return (
    <Helmet>
      {canManageHead && (
        <>
          <title>{formattedTitle}</title>
          <meta name="description" content={finalDescription} />
          {finalKeywords.length > 0 && <meta name="keywords" content={finalKeywords.join(', ')} />}
          {robotsContent && <meta name="robots" content={robotsContent} />}
          <link rel="canonical" href={finalCanonical} />

          {hreflangTags.map((tag, index) => (
            <link key={index} rel={tag.rel} hreflang={tag.hreflang} href={tag.href} />
          ))}

          <meta property="og:title" content={finalOgTitle} />
          <meta property="og:description" content={finalOgDescription} />
          <meta property="og:image" content={finalOgImage} />
          <meta property="og:url" content={finalOgUrl} />
          <meta property="og:type" content={finalOgType} />
          <meta property="og:site_name" content={seoConfig.siteName} />
          {seoConfig.facebookAppId && <meta property="fb:app_id" content={seoConfig.facebookAppId} />}

          <meta name="twitter:card" content={twitterCard} />
          <meta name="twitter:title" content={finalTwitterTitle} />
          <meta name="twitter:description" content={finalTwitterDescription} />
          <meta name="twitter:image" content={finalTwitterImage} />
          {seoConfig.twitterHandle && <meta name="twitter:site" content={seoConfig.twitterHandle} />}

          {Array.isArray(schema)
            ? schema
                .filter(Boolean)
                .map((item, index) => (
                  <script key={index} type="application/ld+json">
                    {JSON.stringify(item)}
                  </script>
                ))
            : schema && (
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
              )}
        </>
      )}
    </Helmet>
  );
};

export default useSEO;
